/**
 * Seo — per-route <head> manager (Jul 2026, P7).
 *
 * A tiny, dependency-free head manager (no react-helmet — keeps the React 19
 * build lean and avoids a portal-based lib). Drop <Seo …/> anywhere in a page
 * and it upserts the document title, description, canonical, Open Graph +
 * Twitter tags, and robots directive for that route. Google renders JS, so it
 * reads everything set here; social scrapers (WhatsApp/FB) that don't run JS
 * still fall back to the static tags in index.html until prerendering lands.
 *
 * Usage:
 *   <Seo
 *     title="Anarkali Sets"
 *     description="Shop handpicked anarkali sets…"
 *     canonical="/c/Anarkali Set"
 *     image="https://…/product.jpg"   // optional; defaults to the brand image
 *     type="product"                   // optional; default "website"
 *     noindex                          // optional; account/checkout pages
 *   />
 *
 * Props are the SOURCE OF TRUTH while the component is mounted; on unmount we
 * intentionally leave the tags in place (the next route's <Seo/> overwrites
 * them), so there's never a flash of missing meta between routes.
 */
import { useEffect } from "react";

export const SITE_NAME = "JnS Creation";
export const SITE_ORIGIN = "https://jns-creation.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-brand.jpg`;
const DEFAULT_DESCRIPTION =
    "Handpicked sarees, lehengas, anarkalis, custom-stitched outfits and AI try-on. Pan-India shipping, COD available.";

function upsertMeta(attr, key, content) {
    if (content == null || content === "") return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", String(content));
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function removeMeta(attr, key) {
    const el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el) el.remove();
}

function absolute(pathOrUrl) {
    if (!pathOrUrl) return null;
    if (/^(data|blob):/i.test(pathOrUrl)) return null;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return SITE_ORIGIN + (pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`);
}

export default function Seo({
    title,
    description,
    canonical,
    image,
    type = "website",
    noindex = false,
}) {
    useEffect(() => {
        const fullTitle = title
            ? `${title} · ${SITE_NAME}`
            : `${SITE_NAME} — Indian Wear Online`;
        const desc = description || DEFAULT_DESCRIPTION;
        const url = canonical
            ? absolute(canonical)
            : (typeof window !== "undefined" ? SITE_ORIGIN + window.location.pathname : SITE_ORIGIN);
        const img = absolute(image) || DEFAULT_IMAGE;
        const imageAlt = title ? `${title} - ${SITE_NAME}` : `${SITE_NAME} Indian wear boutique`;

        document.title = fullTitle;
        upsertMeta("name", "description", desc);
        upsertLink("canonical", url);
        upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

        // Open Graph
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:description", desc);
        upsertMeta("property", "og:type", type === "product" ? "product" : "website");
        upsertMeta("property", "og:url", url);
        upsertMeta("property", "og:image", img);
        upsertMeta("property", "og:image:alt", imageAlt);
        if (img === DEFAULT_IMAGE) {
            upsertMeta("property", "og:image:width", "1200");
            upsertMeta("property", "og:image:height", "630");
        } else {
            removeMeta("property", "og:image:width");
            removeMeta("property", "og:image:height");
        }
        upsertMeta("property", "og:site_name", SITE_NAME);

        // Twitter
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", fullTitle);
        upsertMeta("name", "twitter:description", desc);
        upsertMeta("name", "twitter:image", img);
        upsertMeta("name", "twitter:image:alt", imageAlt);
    }, [title, description, canonical, image, type, noindex]);

    return null;
}
